import { useQueries, useQuery } from '@tanstack/react-query'
import { QueryKeys } from 'constants/query-keys'
import { BrandSalesProps, BrandsProps } from 'types/api'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import {
    fetchBrandSales,
    fetchBrandsGreenFields,
    fetchBrandsLoyalty,
    SalesPayload,
} from 'queries/brands/brand-sales'
import { Constants } from 'constants/constants'
import { isCompany, isStore } from 'utils/location'
import {
    getGreenfieldDrillValues,
    getGreenfieldLocationFilter,
} from 'utils/greenfield'
import { IGenericResponse } from 'utils/api'

export function useGetBrandSales({
    env,
    accessToken,
    location,
    filters,
    brand,
}: BrandSalesProps): IGenericResponse<SalesPayload> {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.BRANDSALES, location, filters, brand],
        () =>
            fetchBrandSales({
                env,
                accessToken,
                filters,
                method: 'GET',
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
            enabled: !!brand,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}

export function useMultipleBrandsGreenfield({
    env,
    accessToken,
    location,
    filters,
    brand,
}: BrandSalesProps) {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isStoreLocation = isStore(location)
    const isCompanyLocation = isCompany(location)
    const getBrandFilterType = (brandType: string): string => {
        switch (brandType) {
            case 'mic':
                return 'MIC Code'
            case 'brand':
                return 'Brand ID'
            case 'licensor':
                return 'Licensor Name'
            case 'department':
                return 'Department ID'
            default:
                return 'Brand ID'
        }
    }

    const greenfieldCardNumbers = [
        {
            key: 'brandMetrics',
            cardNumber: Constants.GREENFIELD_CARDS.BRANDS.BRAND_METRICS,
        },
        ...(isStoreLocation
            ? []
            : [
                  {
                      key: 'brandLocation',
                      cardNumber:
                          Constants.GREENFIELD_CARDS.BRANDS.BRAND_LOCATIONS,
                  },
              ]),
    ]
    const getFiltersForKey = (key: string): Record<string, any> => {
        const drillFilters = getGreenfieldDrillValues(location)
        const brandFilters = {
            filter_value: brand.brand_ref_id_arr.join(','),
            filter_dimension: getBrandFilterType(brand.brand_mic_licensor),
        }

        const baseFilters = {
            ...filters,
            ...(!isCompanyLocation && getGreenfieldLocationFilter(location)),
            ...(!isCompanyLocation && drillFilters),
            ...{ prepend_footer_to_rows: 'true' },
        }
        switch (key) {
            case 'brandLocation':
                return {
                    ...baseFilters,
                    ...brandFilters,
                }
            case 'brandMetrics':
                return {
                    ...filters,
                    ...(!isCompanyLocation &&
                        getGreenfieldLocationFilter(location)),
                    ...brandFilters,
                }
            default:
                return filters || {}
        }
    }
    const queries = greenfieldCardNumbers.map(({ key, cardNumber }) => ({
        queryKey: [
            QueryKeys.BRANDSGREENFIELD,
            location,
            cardNumber,
            filters,
            brand,
        ],
        queryFn: () =>
            fetchBrandsGreenFields({
                env,
                accessToken,
                dynamicSubPath: cardNumber,
                method: 'GET',
                filters: getFiltersForKey(key),
                trackEvent,
            }),
        staleTime: tenMinutes,
        cacheTime: tenMinutes,
        retry: false,
        enabled: !!brand,
    }))
    const results = useQueries({ queries })
    const brandMetrics = results[0]
    const brandLocation = results[1]
    return { brandLocation, brandMetrics }
}

export const useUltaLoyalty = ({
    env,
    accessToken,
    location,
    filters,
}: BrandsProps) => {
    const { tenMinutes } = TIME
    const { trackEvent } = useAnalytics()
    const isCompanyLocation = isCompany(location)
    const cardNumber = Constants.GREENFIELD_CARDS.BRANDS.BRAND_ULTA_LOYALTY
    const drillFilters = {
        ...(!isCompanyLocation && getGreenfieldLocationFilter(location)),
        ...(!isCompanyLocation && getGreenfieldDrillValues(location)),
        ...filters,
    }

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.BRANDS_LOYALTY, location, filters],
        () =>
            fetchBrandsLoyalty({
                env,
                accessToken,
                filters: drillFilters,
                dynamicSubPath: cardNumber,
                method: 'GET',
                trackEvent,
            }),
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            enabled: filters?.time_dimension !== 'today',
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}
